import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Boxes, History } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { useDuplicateCheck } from "@/hooks/useDuplicateCheck";
import { inventoryService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";

const EMPTY = {
  store_id: "",
  sku: "",
  product_name: "",
  description: "",
  available: 0,
  min_threshold: 5,
  bin_location: "",
  shelf_location: "",
  notes: "",
};

export function InventoryDrawer({ open, onOpenChange, record, stores = [], writable = true, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;
  const readOnly = !writable;
  const timelineQuery = useQuery({
    queryKey: ["inventory", "transactions", record?.id],
    queryFn: () => inventoryService.transactions(record.id),
    enabled: open && isEdit,
  });
  const timeline = timelineQuery.data?.items || [];

  useEffect(() => {
    if (open) {
      if (record) {
        setForm({
          store_id: record.store_id || "",
          sku: record.sku || "",
          product_name: record.product_name || "",
          description: record.description || "",
          available: record.available ?? 0,
          min_threshold: record.min_threshold ?? 5,
          bin_location: record.bin_location || "",
          shelf_location: record.shelf_location || "",
          notes: record.notes || "",
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const { result: dup } = useDuplicateCheck(
    () => inventoryService.checkDuplicate({ store_id: form.store_id, sku: form.sku, exclude_id: record?.id || "" }),
    [form.store_id, form.sku, open],
    open && !isEdit && !!form.store_id && !!form.sku.trim()
  );
  const skuConflict = dup?.sku_conflict;

  const storeError = !form.store_id ? "Store is required" : "";
  const skuError = !form.sku.trim() ? "SKU is required" : skuConflict ? "This SKU already exists for this store" : "";
  const productError = !form.product_name.trim() ? "Product name is required" : "";
  const canSave = !storeError && !skuError && !productError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (isEdit) {
        await inventoryService.update(record.id, {
          product_name: form.product_name.trim(),
          description: form.description.trim(),
          min_threshold: Number(form.min_threshold),
          bin_location: form.bin_location.trim(),
          shelf_location: form.shelf_location.trim(),
          notes: form.notes.trim(),
        });
        toast.success("Inventory item updated");
      } else {
        await inventoryService.create({
          store_id: form.store_id,
          sku: form.sku.trim(),
          product_name: form.product_name.trim(),
          description: form.description.trim(),
          available: Number(form.available) || 0,
          min_threshold: Number(form.min_threshold),
          bin_location: form.bin_location.trim(),
          shelf_location: form.shelf_location.trim(),
          notes: form.notes.trim(),
        });
        toast.success("Inventory item created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const storeName = stores.find((s) => s.id === form.store_id)?.store_name;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Boxes}
      title={isEdit ? `Edit ${record?.sku || "Item"}` : "New Inventory Item"}
      description={isEdit ? "Update product details. Use Adjust Inventory to change stock." : "Add a stock item to a store."}
      widthClass="sm:max-w-xl"
      footer={
        readOnly ? (
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="inventory-drawer-close">Close</Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="inventory-drawer-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={!canSave} data-testid="inventory-drawer-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create item"}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Item details">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Store" required error={storeError}>
              {isEdit ? (
                <Input value={storeName || "—"} disabled data-testid="inventory-store-readonly" />
              ) : (
                <Select value={form.store_id} onValueChange={set("store_id")}>
                  <SelectTrigger data-testid="inventory-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="SKU" required error={skuError}>
              <Input value={form.sku} onChange={(e) => set("sku")(e.target.value)} disabled={isEdit}
                placeholder="FF-100" data-testid="inventory-sku-input" />
            </Field>
          </div>
          <Field label="Product Name" required error={productError}>
            <Input value={form.product_name} onChange={(e) => set("product_name")(e.target.value)} placeholder="Widget" data-testid="inventory-product-input" />
          </Field>
          <Field label="Description">
            <Textarea value={form.description} onChange={(e) => set("description")(e.target.value)} rows={2} data-testid="inventory-description-input" />
          </Field>
        </DrawerSection>

        <DrawerSection title="Stock">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Available Quantity" hint={isEdit ? "Read-only. Use Adjust Inventory to change stock." : "Initial stock on hand."}>
              <Input type="number" min={0} value={form.available} onChange={(e) => set("available")(e.target.value)}
                disabled={isEdit} data-testid="inventory-available-input" />
            </Field>
            <Field label="Low-Stock Threshold" hint="Alert when available is at or below this.">
              <Input type="number" min={0} value={form.min_threshold} onChange={(e) => set("min_threshold")(e.target.value)} data-testid="inventory-threshold-input" />
            </Field>
          </div>
        </DrawerSection>

        <DrawerSection title="Location">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Bin Location"><Input value={form.bin_location} onChange={(e) => set("bin_location")(e.target.value)} placeholder="A-12" data-testid="inventory-bin-input" /></Field>
            <Field label="Shelf Location"><Input value={form.shelf_location} onChange={(e) => set("shelf_location")(e.target.value)} placeholder="S-04" data-testid="inventory-shelf-input" /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="inventory-notes-input" /></Field>
        </DrawerSection>

        {isEdit && (
          <DrawerSection title="Timeline">
            {timelineQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : timeline.length === 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground" data-testid="inventory-timeline-empty">
                <History className="h-4 w-4" /> No stock movements yet.
              </div>
            ) : (
              <div className="space-y-2" data-testid="inventory-timeline">
                {timeline.map((t) => (
                  <div key={t.id} className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{t.adjustment_label || t.source}</span>
                      <span className="text-sm font-semibold tabular-nums text-foreground">{t.previous_available} → {t.new_available}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t.reason || "—"} · {t.actor || "System"} · {formatRelativeTime(t.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </DrawerSection>
        )}
      </div>
    </Drawer>
  );
}
